import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { ThemeProvider, ToastProvider, TooltipProvider } from '@lawbee/ui';
import { StrictMode, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { billing } from '@lawbee/domain';
import { API_BASE } from './lib/api';
import { createQueryClient } from './lib/query';
import { createAppRouter } from './router';
import '@fontsource-variable/instrument-sans';
import '@fontsource-variable/jetbrains-mono';
import './index.css';

function App() {
  const [queryClient] = useState(createQueryClient);
  const [router] = useState(() => createAppRouter(queryClient));
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

/**
 * Plan prices are edited in Hive (and read from Stripe by the API); apply them to the bundled plan
 * catalog before the first render so every price on screen matches Checkout. Without an answer in
 * 2 s the catalog's own prices are shown.
 */
async function loadPricing(): Promise<void> {
  try {
    const res = await fetch(`${API_BASE.replace(/\/$/, '')}/api/public/pricing`, { signal: AbortSignal.timeout(2000) });
    if (res.ok) billing.applyStandalonePrices(((await res.json()) as { amounts?: Record<string, number> }).amounts ?? {});
  } catch {
    // Keep the catalog prices.
  }
}

const el = document.getElementById('root');
if (!el) throw new Error('#root element missing');
await loadPricing();
createRoot(el).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
